/**
 * Capsule 4 — Combined Intelligence Card.
 * One-glance summary of the user's CapScan + Coaching + AXIS + Assessments.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Activity, Brain, Target, Sparkles, TrendingUp, TrendingDown, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function MiniStat({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-navy/5 p-3">
      <p className="font-dm text-navy/40 text-[9px] uppercase tracking-wider font-bold mb-1">{label}</p>
      <p className="font-playfair text-navy text-xl font-bold leading-none">{value}</p>
      {sub && <p className="font-dm text-navy/40 text-[10px] mt-1">{sub}</p>}
    </div>
  );
}

export default function CombinedIntelligenceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/me/combined-intelligence`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const hasAny = data.capscan || data.coaching || data.axis || data.assessment;
  if (!hasAny) return (
    <div className="bg-gradient-to-br from-navy to-navy/90 text-white rounded-2xl p-6 shadow-xl" data-testid="intel-card-empty">
      <Sparkles className="w-6 h-6 text-gold mb-3" />
      <h3 className="font-playfair text-xl font-bold mb-2">Your Intelligence Card</h3>
      <p className="font-dm text-white/70 text-sm mb-4">Complete a CapScan or coaching session to start building your personalised development intelligence.</p>
      <Link to="/capscan" className="inline-block bg-gold text-navy font-dm font-bold text-sm px-4 py-2 rounded-lg">Start CapScan →</Link>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-navy to-navy/90 text-white rounded-2xl p-6 shadow-xl" data-testid="intel-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="font-dm text-gold text-[10px] uppercase tracking-wider font-bold mb-1">Your Intelligence</p>
          <h3 className="font-playfair text-xl font-bold">Where you are right now, {data.user_name?.split(" ")[0]}</h3>
        </div>
        <Sparkles className="w-5 h-5 text-gold" />
      </div>

      {/* Insight */}
      {data.insight && (
        <div className="bg-gold/10 border-l-4 border-gold rounded-lg p-3 mb-5">
          <p className="font-dm text-white text-sm leading-relaxed">{data.insight}</p>
        </div>
      )}

      {/* Movement headline — surfaces latest re-scan delta */}
      {data.movement && (
        <Link to={`/capscan/movement-report/${data.movement.report_id}`} data-testid="intel-card-movement"
          className="block bg-white/10 hover:bg-white/15 rounded-xl px-4 py-3 mb-4 border border-gold/30 transition-colors">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              {data.movement.delta >= 0
                ? <TrendingUp className="w-4 h-4 text-green-300 flex-shrink-0" />
                : <TrendingDown className="w-4 h-4 text-red-300 flex-shrink-0" />}
              <div className="min-w-0">
                <p className="font-dm text-gold text-[10px] uppercase tracking-wider font-bold leading-none mb-1">Latest movement</p>
                <p className="font-dm text-white text-sm font-bold truncate">{data.movement.employee_name} · {data.movement.label}</p>
                <p className="font-dm text-white/60 text-[11px]">{data.movement.present_fit_then}% → {data.movement.present_fit_now}% Present Fit</p>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className={`font-playfair text-2xl font-bold leading-none ${data.movement.delta >= 0 ? "text-green-300" : "text-red-300"}`}>
                {data.movement.delta > 0 ? "+" : ""}{data.movement.delta}%
              </p>
              <p className="font-dm text-white/40 text-[9px] uppercase mt-1">View report →</p>
            </div>
          </div>
        </Link>
      )}

      {/* Mini stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {data.capscan && <MiniStat label="CapScan gaps" value={data.capscan.critical_gaps_count || 0} sub={`out of ${data.capscan.total_skills} skills`} />}
        {data.coaching && <MiniStat label="Coaching sessions" value={data.coaching.total_sessions} sub={`+${data.coaching.avg_confidence_delta ?? 0} avg confidence`} />}
        {data.axis && <MiniStat label="AXIS style" value={data.axis.dominant_style || "—"} sub={`Secondary: ${data.axis.secondary_style || "—"}`} />}
        {data.assessment && <MiniStat label="Assessment score" value={data.assessment.latest_avg_score?.toFixed(1) || "—"} sub={`${data.assessment.total} taken`} />}
      </div>

      {/* Top gaps */}
      {data.capscan?.top_gaps?.length > 0 && (
        <div className="mb-4">
          <p className="font-dm text-white/50 text-[10px] uppercase tracking-wider font-bold mb-2">Top development priorities</p>
          <div className="space-y-1.5">
            {data.capscan.top_gaps.slice(0, 3).map((g, i) => (
              <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                <span className="font-dm text-sm">{g.skill}</span>
                <span className="font-dm text-xs text-gold">Zone {g.current} → {g.required}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Link to="/coaching" className="bg-gold text-navy font-dm font-bold text-xs px-4 py-2 rounded-lg hover:bg-gold/90 flex items-center gap-1">
          Start coaching <ChevronRight className="w-3 h-3" />
        </Link>
        <Link to="/capscan" className="bg-white/10 hover:bg-white/15 text-white font-dm font-bold text-xs px-4 py-2 rounded-lg">View CapScan</Link>
      </div>
    </div>
  );
}
